<script>
  import { onMount } from 'svelte';
  import { Modal } from 'carbon-components-svelte';
  import { useNavigate } from 'svelte-navigator';

  export let when = false, 
    heading = 'Leave this page?',
    primaryButtonText = 'Leave',
    secondaryButtonText = 'Stay',
    danger = true;

  const navigate = useNavigate();
  const hasNavigationApi = typeof window !== 'undefined' && 'navigation' in window;

  let open = false,
    pendingTarget = null,
    allowedTarget = null;

  function toInAppTarget(href) {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) {
      return null;
    }

    return `${url.pathname}${url.search}${url.hash}`;
  }

  function onBeforeUnload(event) {
    if (!when) {
      return;
    }

    event.preventDefault();
    event.returnValue = '';
  }

  function onNavigate(event) {
    // only guard in-app navigations; full-document navigations
    // (e.g. sign-out via window.location) are left to the beforeunload prompt.
    if (
      !when ||
      !event.canIntercept ||
      !event.destination.sameDocument ||
      event.hashChange ||
      event.downloadRequest !== null
    ) {
      return;
    }

    const target = toInAppTarget(event.destination.url);
    if (target === null) {
      return;
    }

    if (allowedTarget !== null && target === allowedTarget) {
      allowedTarget = null;
      return;
    }

    event.preventDefault();
    pendingTarget = target;
    open = true;
  }

  function onDocumentClickCapture(event) {
    if (!when) {
      return;
    }

    const isPlainClick =
      event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
    if (!isPlainClick) {
      return;
    }

    const anchor = event.target.closest?.('a[href]');
    if (!anchor || anchor.target === '_blank') {
      return;
    }

    const target = toInAppTarget(anchor.href);
    if (target === null) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    pendingTarget = target;
    open = true;
  }

  function confirmLeave() {
    const target = pendingTarget;
    open = false;
    pendingTarget = null;

    if (target !== null) {
      allowedTarget = target;
      navigate(target);
    }
  }

  function cancelLeave() {
    open = false;
    pendingTarget = null;
    allowedTarget = null;
  }

  export function allowNext(target) {
    allowedTarget = target == null ? null : toInAppTarget(new URL(target, window.location.origin).href);
  }

  onMount(() => {
    window.addEventListener('beforeunload', onBeforeUnload);
    if (hasNavigationApi) {
      window.navigation.addEventListener('navigate', onNavigate);
    } else {
      document.addEventListener('click', onDocumentClickCapture, true);
    }

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      if (hasNavigationApi) {
        window.navigation.removeEventListener('navigate', onNavigate);
      } else {
        document.removeEventListener('click', onDocumentClickCapture, true);
      }
    };
  });
</script>

<Modal
  {danger}
  {open}
  modalHeading={heading}
  {primaryButtonText}
  {secondaryButtonText}
  on:click:button--primary={confirmLeave}
  on:click:button--secondary={cancelLeave}
  on:close={cancelLeave}
>
  <slot>
    <p>You have unsaved changes. If you leave, your changes will be lost.</p>
  </slot>
</Modal>
